import { defineStore } from "pinia";
import { ref } from "vue";

import { emptyBoardIteration } from "@/backend/read/mapBackendData";
import type { IdMap } from "@/model/baseTypes";
import type { BoardData, BoardIteration } from "@/model/board";

export const useIterationStore = defineStore("iteration", () => {
  const iterationMap = ref<IdMap<BoardIteration[]>>({});

  function iterations(board: BoardData<"team">): BoardIteration[] {
    return iterationMap.value[board.id] || [];
  }

  function iteration(board: BoardData<"team">, iter: number): BoardIteration {
    // On a slow connection, it's possible the iterations haven't loaded yet
    // so we return an empty iteration in (the UI will update reactively
    // when the data arrives)
    return iterations(board)[iter] || emptyBoardIteration();
  }

  function updateIteration(
    boardId: string,
    iter: number,
    update: (iter: BoardIteration) => BoardIteration,
  ) {
    const iters = iterationMap.value[boardId];
    if (!iters?.[iter]) {
      // Iterations not loaded yet for this board, skip update
      return;
    }
    iters[iter] = update(iters[iter]);
  }

  function setIterations(iterations: Record<string, BoardIteration[]>) {
    iterationMap.value = iterations;
  }

  function setBoards(boardsMap: Record<string, BoardIteration[]>) {
    for (const boardId in boardsMap) {
      iterationMap.value[boardId] = boardsMap[boardId];
    }
  }

  function $reset() {
    iterationMap.value = {};
  }

  return { iterations, iteration, updateIteration, setIterations, setBoards, $reset };
});
